// F-U (2026-10-05) / F-W: search + multi-select filters for the Exhibits
// and Zones pages (each page picks which filters its bar shows).
//
// Pure functions so the matching / cascading rules are unit-testable without
// rendering. Filtering is client-side: the staff list is uncapped and small
// (low thousands at most), and every row already carries its zone,
// exhibition, period and department (server annotation).
//
// Rules (approved design):
// - Search: substring, case-insensitive, trimmed, over the Exhibit Code and
//   the TC / EN / SC titles. An exact code match sorts first.
// - Filters: OR within one filter, AND across filters (and with search).
// - Year: the exhibition period overlaps the year. NONE = "No period set".
// - Exhibition / Zone: NONE = "Unassigned" (exhibit in no zone).
// - Department: NONE = "No department".
// - Cascade: Year + Department narrow the Exhibition options; Exhibition
//   (with Year + Department) narrows the Zone options. Selections that no
//   longer fit are dropped by `pruneFilters`.
// - Options come from the loaded rows (decision 2).

import { periodYears } from "@shared/lib/period";

/** The row fields the filters + search read (exhibit and zone list rows). */
export interface FilterableRow {
  id: string;
  code?: string | null;
  titleTc?: string | null;
  titleEn?: string | null;
  titleSc?: string | null;
  zoneId?: string | null;
  zoneTitleTc?: string | null;
  exhibitionId?: string | null;
  exhibitionTitleTc?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
}

/** Sentinel option value for "Unassigned" / "No period set" / "No department". */
export const NONE = "_none";

export type FilterKey = "years" | "exhibitions" | "zones" | "departments";

export const FILTER_KEYS: FilterKey[] = [
  "years",
  "exhibitions",
  "zones",
  "departments",
];

export interface ExhibitFilters {
  years: string[];
  exhibitions: string[];
  zones: string[];
  departments: string[];
}

export const EMPTY_FILTERS: ExhibitFilters = {
  years: [],
  exhibitions: [],
  zones: [],
  departments: [],
};

export interface FilterOption {
  value: string;
  label: string;
  /** Zone options carry their exhibition's title for grouping. */
  group?: string;
}

export type FilterOptions = Record<FilterKey, FilterOption[]>;

// ── URL state ────────────────────────────────────────────────────────────

const PARAM: Record<FilterKey, string> = {
  years: "year",
  exhibitions: "exhibition",
  zones: "zone",
  departments: "dept",
};

const splitParam = (v: string | null): string[] =>
  v ? v.split(",").filter(Boolean) : [];

export function filtersFromParams(params: URLSearchParams): ExhibitFilters {
  return {
    years: splitParam(params.get(PARAM.years)),
    exhibitions: splitParam(params.get(PARAM.exhibitions)),
    zones: splitParam(params.get(PARAM.zones)),
    departments: splitParam(params.get(PARAM.departments)),
  };
}

/** Writes the filters into a copy of `params` (other keys kept). */
export function filtersToParams(
  filters: ExhibitFilters,
  params: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of FILTER_KEYS) {
    if (filters[key].length) next.set(PARAM[key], filters[key].join(","));
    else next.delete(PARAM[key]);
  }
  return next;
}

export const activeFilterCount = (f: ExhibitFilters): number =>
  FILTER_KEYS.reduce((n, k) => n + f[k].length, 0);

// ── Per-row facets ───────────────────────────────────────────────────────

const yearsOf = (row: FilterableRow): string[] => {
  const years = periodYears(row.periodStart, row.periodEnd);
  return years.length ? years.map(String) : [NONE];
};
const exhibitionOf = (row: FilterableRow): string => row.exhibitionId || NONE;
const zoneOf = (row: FilterableRow): string => row.zoneId || NONE;
const departmentOf = (row: FilterableRow): string => row.departmentId || NONE;

const passes = (selected: string[], values: string[]): boolean =>
  selected.length === 0 || values.some((v) => selected.includes(v));

function matchesFilters(
  row: FilterableRow,
  f: ExhibitFilters,
  only: FilterKey[] = FILTER_KEYS,
): boolean {
  return only.every((key) => {
    switch (key) {
      case "years":
        return passes(f.years, yearsOf(row));
      case "exhibitions":
        return passes(f.exhibitions, [exhibitionOf(row)]);
      case "zones":
        return passes(f.zones, [zoneOf(row)]);
      case "departments":
        return passes(f.departments, [departmentOf(row)]);
    }
  });
}

// ── Search ───────────────────────────────────────────────────────────────

export const normalizeQuery = (q: string): string => q.trim().toLowerCase();

export function matchesSearch(row: FilterableRow, q: string): boolean {
  const needle = normalizeQuery(q);
  if (!needle) return true;
  return [row.code, row.titleTc, row.titleEn, row.titleSc].some((field) =>
    (field ?? "").toLowerCase().includes(needle),
  );
}

/** Rows passing search + every filter. Exact code matches first; otherwise
 *  the incoming (newest-first) order is kept. */
export function applyFilters<T extends FilterableRow>(
  rows: T[],
  q: string,
  f: ExhibitFilters,
): T[] {
  const needle = normalizeQuery(q);
  const out = rows.filter(
    (row) => matchesSearch(row, needle) && matchesFilters(row, f),
  );
  if (!needle) return out;
  const exact = out.filter((r) => (r.code ?? "").toLowerCase() === needle);
  if (!exact.length) return out;
  return [...exact, ...out.filter((r) => !exact.includes(r))];
}

// ── Options (cascading) ──────────────────────────────────────────────────

const byLabel = (a: FilterOption, b: FilterOption) =>
  a.label.localeCompare(b.label);

/** Options for each dropdown, derived from the loaded rows. The NONE option
 *  is listed last, and only when some row would match it. */
export function buildOptions(
  rows: FilterableRow[],
  f: ExhibitFilters,
): FilterOptions {
  // Year + Department are top-level: options from every row.
  const years = new Set<string>();
  const departments = new Map<string, string>();
  for (const row of rows) {
    yearsOf(row).forEach((y) => years.add(y));
    departments.set(
      departmentOf(row),
      row.departmentName ?? row.departmentId ?? "",
    );
  }

  // Exhibition: rows passing Year + Department.
  const exhibitions = new Map<string, string>();
  for (const row of rows) {
    if (!matchesFilters(row, f, ["years", "departments"])) continue;
    exhibitions.set(exhibitionOf(row), row.exhibitionTitleTc ?? "");
  }

  // Zone: rows passing Year + Department + Exhibition.
  const zones = new Map<string, FilterOption>();
  for (const row of rows) {
    if (!matchesFilters(row, f, ["years", "departments", "exhibitions"]))
      continue;
    const value = zoneOf(row);
    zones.set(value, {
      value,
      label: row.zoneTitleTc || (row.zoneId ?? ""),
      group: row.exhibitionTitleTc ?? undefined,
    });
  }

  const withNone = (
    opts: FilterOption[],
    hasNone: boolean,
    noneLabel: string,
  ): FilterOption[] =>
    hasNone ? [...opts, { value: NONE, label: noneLabel }] : opts;

  return {
    years: withNone(
      [...years]
        .filter((y) => y !== NONE)
        .sort((a, b) => Number(b) - Number(a))
        .map((y) => ({ value: y, label: y })),
      years.has(NONE),
      "No period set",
    ),
    exhibitions: withNone(
      [...exhibitions]
        .filter(([v]) => v !== NONE)
        .map(([value, label]) => ({ value, label: label || value }))
        .sort(byLabel),
      exhibitions.has(NONE),
      "Unassigned",
    ),
    zones: withNone(
      [...zones.values()]
        .filter((o) => o.value !== NONE)
        .sort(
          (a, b) =>
            (a.group ?? "").localeCompare(b.group ?? "") || byLabel(a, b),
        ),
      zones.has(NONE),
      "Unassigned",
    ),
    departments: withNone(
      [...departments]
        .filter(([v]) => v !== NONE)
        .map(([value, label]) => ({ value, label: label || value }))
        .sort(byLabel),
      departments.has(NONE),
      "No department",
    ),
  };
}

/** Drop selections that are no longer offered (cascade), repeating until
 *  stable — pruning an exhibition can in turn invalidate a zone. Returns the
 *  pruned filters, the matching options, and how many values were removed
 *  per key. With no rows loaded yet nothing is pruned, so a deep link isn't
 *  wiped before the data arrives. */
export function reconcileFilters(
  rows: FilterableRow[],
  f: ExhibitFilters,
): {
  filters: ExhibitFilters;
  options: FilterOptions;
  removed: Partial<Record<FilterKey, number>>;
} {
  let filters = f;
  let options = buildOptions(rows, filters);
  const removed: Partial<Record<FilterKey, number>> = {};
  if (!rows.length) return { filters, options, removed };
  for (let pass = 0; pass < FILTER_KEYS.length; pass += 1) {
    let changed = false;
    const next = { ...filters };
    for (const key of FILTER_KEYS) {
      const offered = new Set(options[key].map((o) => o.value));
      const kept = filters[key].filter((v) => offered.has(v));
      if (kept.length !== filters[key].length) {
        removed[key] = (removed[key] ?? 0) + filters[key].length - kept.length;
        next[key] = kept;
        changed = true;
      }
    }
    if (!changed) break;
    filters = next;
    options = buildOptions(rows, filters);
  }
  return { filters, options, removed };
}

/** Label for a selected value (chips), falling back to the raw value. */
export function optionLabel(
  options: FilterOptions,
  key: FilterKey,
  value: string,
): string {
  return options[key].find((o) => o.value === value)?.label ?? value;
}
